/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from 'sweetalert2';
import { formatDateDDMMYYYY } from '../../utils/dateUtils';
import {
  Grid,
  Card,
  TextField,
  Box,
  Button,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tab,
  Tabs,
  Paper,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Medication as MedicationIcon,
  Assignment as AssignmentIcon,
  LocalHospital as LocalHospitalIcon,
  Visibility as ViewIcon,
  CameraAlt as CameraAltIcon,
  Healing as HealingIcon,
  EventNote as EventNoteIcon,
  DocumentScanner as ScannerIcon,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import LoaderOverlay from "components/LoaderOverlay";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import FollowUpDialog from "components/FollowUpDialog";
import ScanPrescriptionDialog from "layouts/prescription/ScanPrescriptionDialog";
import { api } from "../../api";

// Constants for consistent styling
const selectBoxHeight = 45;

function ManagePrescription() {
  const navigate = useNavigate();
  const { patientCode, coupleCode, prescriptionCode } = useParams();

  // State management
  const [loading, setLoading] = useState(false);
  const [prescription, setPrescription] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [components, setComponents] = useState([]);
  
  // Form states - now using arrays for each component type
  const [activeTab, setActiveTab] = useState(0);
  
  // Existing components from database
  const [existingComponents, setExistingComponents] = useState({
    prescriptionMedications: [],
    prescriptionInvestigations: [],
    prescriptionObservations: [],
    prescriptionScans: [],
    prescriptionTreatments: []
  });
  
  // New components added through forms (to be saved)
  const [newComponents, setNewComponents] = useState({
    prescriptionMedications: [],
    prescriptionInvestigations: [],
    prescriptionObservations: [],
    prescriptionScans: [],
    prescriptionTreatments: []
  });
  
  // Combined view for display (existing + new)
  const [prescriptionComponents, setPrescriptionComponents] = useState({
    prescriptionMedications: [],
    prescriptionInvestigations: [],
    prescriptionObservations: [],
    prescriptionScans: [],
    prescriptionTreatments: []
  });
  
  // Individual component form states
  const [medicationForm, setMedicationForm] = useState({
    name: '',
    dosage: '',
    frequency: '',
    duration: '',
    instructions: '',
    quantity: 1,
    route: ''
  });
  
  const [investigationForm, setInvestigationForm] = useState({
    name: '',
    description: '',
    instructions: '',
    priority: 'NORMAL',
    status: 'PENDING'
  });
  
  const [observationForm, setObservationForm] = useState({
    name: '',
    value: '',
    description: ''
  });
  
  const [scanForm, setScanForm] = useState({
    name: '',
    description: '',
    instructions: '',
    priority: 'NORMAL',
    status: 'PENDING'
  });
  
  const [treatmentForm, setTreatmentForm] = useState({
    name: '',
    description: '',
    instructions: '',
    duration: '',
    frequency: '',
    status: 'ACTIVE'
  });

  // Follow-up state
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [defaultDoctorId, setDefaultDoctorId] = useState(null);

  // AI Scan state
  const [scanDialogOpen, setScanDialogOpen] = useState(false);

  // Logged-in user id from localStorage
  const currentUserId = (() => {
    try {
      const raw = localStorage.getItem('user') || localStorage.getItem('userData');
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed?.user_id || parsed?.id || 1;
      }
    } catch { /* ignore */ }
    return 1;
  })();

  // Load prescription and related data
  useEffect(() => {
    if (prescriptionCode && patientCode) {
      loadPrescriptionData();
      loadPatientInfo();
    }
  }, [prescriptionCode, patientCode, coupleCode]);

  const loadPatientInfo = async () => {
    try {
      // Get patient info using the contact API
      const response = await api.contact.getContactByExternalCode(patientCode);
      if (response?.data?.data) {
        setPatientInfo(response.data.data);
      } else if (response?.data) {
        // Fallback for different response structure
        setPatientInfo(response.data);
      }
    } catch (error) {
      console.error("Error loading patient info:", error);
    }
  };

  const loadDoctorsForFollowUp = async (contactId) => {
    try {
      // Load all users as doctor options
      const usersResp = await api.user.getActiveUsers ? api.user.getActiveUsers() : null;
      let doctorList = [];
      if (usersResp?.data?.data) {
        doctorList = usersResp.data.data.map(u => ({ id: u.id, name: u.name || u.full_name || `User #${u.id}` }));
      }
      setDoctors(doctorList);

      // Fetch default doctor (last prescription doctor)
      if (contactId) {
        const drResp = await api.opd.getLastDoctorForContact(contactId);
        const drId = drResp?.data?.data?.doctor_id || null;
        // Default: last prescription doctor = current prescription doctor
        setDefaultDoctorId(drId || prescription?.irm_users_id || null);
      }
    } catch (err) {
      console.error("Error loading doctors:", err);
      // Fallback: pre-fill with prescription doctor
      if (prescription?.irm_users_id) {
        setDefaultDoctorId(prescription.irm_users_id);
        setDoctors([{ id: prescription.irm_users_id, name: `Doctor #${prescription.irm_users_id}` }]);
      }
    }
  };

  const loadPrescriptionData = async () => {
    setLoading(true);
    try {
      // Use the new API to get prescription details with all active components
      const prescriptionResponse = await api.opd.getPrescriptionDetailsByCode(prescriptionCode);
      if (prescriptionResponse?.data) {
        const prescriptionData = prescriptionResponse.data;
        setPrescription(prescriptionData);
        
        // Set existing components from database
        const existing = {
          prescriptionMedications: prescriptionData.prescriptionMedications || [],
          prescriptionInvestigations: prescriptionData.prescriptionInvestigations || [],
          prescriptionObservations: prescriptionData.prescriptionObservations || [],
          prescriptionScans: prescriptionData.prescriptionScans || [],
          prescriptionTreatments: prescriptionData.prescriptionTreatments || []
        };
        
        setExistingComponents(existing);
        
        // Initially, prescriptionComponents only shows existing data
        setPrescriptionComponents(existing);
        
        // Reset new components when loading fresh data
        setNewComponents({
          prescriptionMedications: [],
          prescriptionInvestigations: [],
          prescriptionObservations: [],
          prescriptionScans: [],
          prescriptionTreatments: []
        });
      }
    } catch (error) {
      console.error("Error loading prescription data:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to load prescription data. Please try again.',
        showConfirmButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle component form changes
  const handleMedicationFormChange = (field, value) => {
    setMedicationForm(prev => ({ ...prev, [field]: value }));
  };

  const handleInvestigationFormChange = (field, value) => {
    setInvestigationForm(prev => ({ ...prev, [field]: value }));
  };

  const handleObservationFormChange = (field, value) => {
    setObservationForm(prev => ({ ...prev, [field]: value }));
  };

  const handleScanFormChange = (field, value) => {
    setScanForm(prev => ({ ...prev, [field]: value }));
  };

  const handleTreatmentFormChange = (field, value) => {
    setTreatmentForm(prev => ({ ...prev, [field]: value }));
  };

  // Helper function to update combined view
  const updateCombinedView = (updatedNewComponents) => {
    const combined = {
      prescriptionMedications: [...existingComponents.prescriptionMedications, ...updatedNewComponents.prescriptionMedications],
      prescriptionInvestigations: [...existingComponents.prescriptionInvestigations, ...updatedNewComponents.prescriptionInvestigations],
      prescriptionObservations: [...existingComponents.prescriptionObservations, ...updatedNewComponents.prescriptionObservations],
      prescriptionScans: [...existingComponents.prescriptionScans, ...updatedNewComponents.prescriptionScans],
      prescriptionTreatments: [...existingComponents.prescriptionTreatments, ...updatedNewComponents.prescriptionTreatments]
    };
    setPrescriptionComponents(combined);
  };

  // Add components to arrays
  const addMedication = () => {
    if (medicationForm.name) {
      const updatedNewComponents = {
        ...newComponents,
        prescriptionMedications: [...newComponents.prescriptionMedications, { ...medicationForm }]
      };
      setNewComponents(updatedNewComponents);
      updateCombinedView(updatedNewComponents);
      
      setMedicationForm({
        name: '',
        dosage: '',
        frequency: '',
        duration: '',
        instructions: '',
        quantity: 1,
        route: ''
      });
    }
  };

  const addInvestigation = () => {
    if (investigationForm.name) {
      const updatedNewComponents = {
        ...newComponents,
        prescriptionInvestigations: [...newComponents.prescriptionInvestigations, { ...investigationForm }]
      };
      setNewComponents(updatedNewComponents);
      updateCombinedView(updatedNewComponents);
      
      setInvestigationForm({
        name: '',
        description: '',
        instructions: '',
        priority: 'NORMAL',
        status: 'PENDING'
      });
    }
  };

  const addObservation = () => {
    if (observationForm.name) {
      const updatedNewComponents = {
        ...newComponents,
        prescriptionObservations: [...newComponents.prescriptionObservations, { ...observationForm }]
      };
      setNewComponents(updatedNewComponents);
      updateCombinedView(updatedNewComponents);
      
      setObservationForm({
        name: '',
        value: '',
        description: ''
      });
    }
  };

  const addScan = () => {
    if (scanForm.name) {
      const updatedNewComponents = {
        ...newComponents,
        prescriptionScans: [...newComponents.prescriptionScans, { ...scanForm }]
      };
      setNewComponents(updatedNewComponents);
      updateCombinedView(updatedNewComponents);
      
      setScanForm({
        name: '',
        description: '',
        instructions: '',
        priority: 'NORMAL',
        status: 'PENDING'
      });
    }
  };

  const addTreatment = () => {
    if (treatmentForm.name) {
      const updatedNewComponents = {
        ...newComponents,
        prescriptionTreatments: [...newComponents.prescriptionTreatments, { ...treatmentForm }]
      };
      setNewComponents(updatedNewComponents);
      updateCombinedView(updatedNewComponents);
      
      setTreatmentForm({
        name: '',
        description: '',
        instructions: '',
        duration: '',
        frequency: '',
        status: 'ACTIVE'
      });
    }
  };

  // Remove components from arrays
  const removeComponent = async (type, index) => {
    const existingLength = existingComponents[type].length;
    
    if (index < existingLength) {
      // Removing an existing component - confirm deletion first
      const component = existingComponents[type][index];
      
      const result = await Swal.fire({
        title: 'Delete Component?',
        text: `Are you sure you want to delete "${component.name}"? This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      });
      
      if (result.isConfirmed) {
        try {
          setLoading(true);
          
          // Map component type to API format
          const componentTypeMap = {
            'prescriptionMedications': 'medication',
            'prescriptionInvestigations': 'investigation',
            'prescriptionObservations': 'observation',
            'prescriptionScans': 'scan',
            'prescriptionTreatments': 'treatment'
          };
          
          const deleteData = {
            component_type: componentTypeMap[type],
            component_id: component.id,
            prescription_id: prescription?.id,
            deleted_by: 1 // You should get this from auth context
          };
          
          console.log('Deleting component with data:', deleteData);
          
          const response = await api.opd.deletePrescriptionComponent(deleteData);
          
          setLoading(false);
          
          if (![200, 201].includes(response.statusCode)) {
            console.error(`Failed to delete prescription component: ${response.message}`);
            await Swal.fire({
              icon: 'error',
              title: 'Error!',
              text: response.message || 'Failed to delete component. Please try again.',
              showConfirmButton: true,
              confirmButtonColor: '#d33',
              confirmButtonText: 'OK'
            });
          } else {
            // Remove from existing components after successful API call
            const updatedExisting = {
              ...existingComponents,
              [type]: existingComponents[type].filter((_, i) => i !== index)
            };
            setExistingComponents(updatedExisting);
            
            // Update combined view
            const combined = {
              ...prescriptionComponents,
              [type]: [...updatedExisting[type], ...newComponents[type]]
            };
            setPrescriptionComponents(combined);
            
            await Swal.fire({
              icon: 'success',
              title: 'Deleted!',
              text: 'Component has been deleted successfully.',
              showConfirmButton: true,
              confirmButtonColor: '#00798E',
              confirmButtonText: 'OK'
            });
          }
        } catch (error) {
          setLoading(false);
          console.error("Error deleting prescription component:", error);
          
          await Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: 'Failed to delete component. Please try again.',
            showConfirmButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'OK'
          });
        }
      }
    } else {
      // Removing a new component - just remove from local state (no API call needed)
      const newIndex = index - existingLength;
      const updatedNewComponents = {
        ...newComponents,
        [type]: newComponents[type].filter((_, i) => i !== newIndex)
      };
      setNewComponents(updatedNewComponents);
      updateCombinedView(updatedNewComponents);
    }
  };

  // ── AI Scan handlers ──────────────────────────────────────────────────────

  const handleAiApplyMedications = (meds) => {
    const updated = {
      ...newComponents,
      prescriptionMedications: [...newComponents.prescriptionMedications, ...meds],
    };
    setNewComponents(updated);
    updateCombinedView(updated);
  };

  const handleAiApplyInvestigations = (invs) => {
    const updated = {
      ...newComponents,
      prescriptionInvestigations: [...newComponents.prescriptionInvestigations, ...invs],
    };
    setNewComponents(updated);
    updateCombinedView(updated);
  };

  const handleAiApplyVitals = async (vitals) => {
    if (!prescription?.id) return;
    const payload = {};
    if (vitals.weight) payload.weight = vitals.weight;
    if (vitals.height) payload.height = vitals.height;
    if (vitals.bp_systolic) payload.bp_systolic = vitals.bp_systolic;
    if (vitals.bp_diastolic) payload.bp_diastolic = vitals.bp_diastolic;
    if (vitals.synopsis) payload.synopsis = vitals.synopsis;
    if (vitals.probable_diagonosis_female) payload.probable_diagonosis_female = vitals.probable_diagonosis_female;
    if (vitals.probable_diagonosis_male) payload.probable_diagonosis_male = vitals.probable_diagonosis_male;
    if (vitals.remarks) payload.remarks = vitals.remarks;

    if (Object.keys(payload).length > 0) {
      try {
        await api.opd.updatePrescription(prescription.id, payload);
        // Refresh to show updated vitals
        await loadPrescriptionData();
      } catch { /* non-critical — vitals are still in the form via applyVitals */ }
    }
  };

  // Save all prescription components
  const handleSavePrescription = async () => {
    try {
      setLoading(true);
      
      // Check if there are any new components to save
      const hasNewComponents = Object.values(newComponents).some(componentArray => componentArray.length > 0);
      
      if (!hasNewComponents) {
        await Swal.fire({
          icon: 'info',
          title: 'No New Components',
          text: 'There are no new components to save.',
          showConfirmButton: true,
          confirmButtonColor: '#00798E',
          confirmButtonText: 'OK'
        });
        setLoading(false);
        return;
      }
      
      // Ensure we have the required IDs
      if (!patientInfo?.id && !prescription?.irm_contacts_id) {
        throw new Error('Patient contact ID not found');
      }
      
      if (!prescription?.id) {
        throw new Error('Prescription ID not found');
      }
      
      // Only send new components (not existing ones from database)
      const componentData = {
        contact_id: patientInfo?.id || prescription?.irm_contacts_id,
        prescription_id: prescription?.id,
        prescription_code: prescriptionCode,
        prescription_medications: newComponents.prescriptionMedications || [],
        prescription_investigations: newComponents.prescriptionInvestigations || [],
        prescription_observations: newComponents.prescriptionObservations || [],
        prescription_scans: newComponents.prescriptionScans || [],
        prescription_treatments: newComponents.prescriptionTreatments || [],
        created_by: 1 // You should get this from auth context
      };

      console.log('Saving only new prescription components with data:', componentData);
      
      const response = await api.opd.managePrescriptionComponents(componentData);
      console.log('Response from save operation:', response);
      
      // Hide loader before showing alerts
      setLoading(false);
      
      if (![200, 201].includes(response.statusCode)) {
        console.error(`Failed to save prescription components: ${response.message}`);
        await Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: response.message,
            showConfirmButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'OK'
        });
      } else {
          await Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: 'New prescription components saved successfully.',
            showConfirmButton: true,
            confirmButtonColor: '#00798E',
            confirmButtonText: 'OK'
          });
          
          // Reload prescription data to get updated components
          await loadPrescriptionData();
      }
    } catch (error) {
      console.error("Error saving prescription components:", error);
      
      // Hide loader before showing error alert
      setLoading(false);
      
      let errorMessage = 'Failed to save prescription components. Please try again.';
      if (error.message.includes('contact ID')) {
        errorMessage = 'Patient information not loaded properly. Please refresh the page and try again.';
      } else if (error.message.includes('Prescription ID')) {
        errorMessage = 'Prescription information not loaded properly. Please refresh the page and try again.';
      }
      
      await Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: errorMessage,
        showConfirmButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    }
    // Remove the finally block since we're handling loading state explicitly
  };

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Get component type icon
  const getComponentIcon = (type) => {
    switch (type) {
      case 'medications':
        return <MedicationIcon />;
      case 'observations':
        return <LocalHospitalIcon />;
      case 'investigations':
        return <AssignmentIcon />;
      case 'scans':
        return <CameraAltIcon />;
      case 'treatments':
        return <HealingIcon />;
      default:
        return <AssignmentIcon />;
    }
  };

  // Render medication form
  const renderMedicationForm = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Medicine Name"
          value={medicationForm.name}
          onChange={(e) => handleMedicationFormChange('name', e.target.value.toUpperCase())}
          variant="outlined"
          margin="normal"
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: selectBoxHeight,
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
            '& .MuiInputBase-input': { 
              textTransform: 'uppercase' 
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <TextField
          fullWidth
          label="Quantity"
          type="number"
          value={medicationForm.quantity}
          onChange={(e) => handleMedicationFormChange('quantity', parseInt(e.target.value) || 1)}
          variant="outlined"
          margin="normal"
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: selectBoxHeight,
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <TextField
          fullWidth
          label="Dosage"
          value={medicationForm.dosage}
          onChange={(e) => handleMedicationFormChange('dosage', e.target.value.toUpperCase())}
          variant="outlined"
          margin="normal"
          placeholder="e.g., 500mg"
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: selectBoxHeight,
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
            '& .MuiInputBase-input': { 
              textTransform: 'uppercase' 
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <TextField
          fullWidth
          label="Frequency"
          value={medicationForm.frequency}
          onChange={(e) => handleMedicationFormChange('frequency', e.target.value.toUpperCase())}
          variant="outlined"
          margin="normal"
          placeholder="e.g., Twice daily"
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: selectBoxHeight,
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
            '& .MuiInputBase-input': { 
              textTransform: 'uppercase' 
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Route"
          value={medicationForm.route}
          onChange={(e) => handleMedicationFormChange('route', e.target.value.toUpperCase())}
          variant="outlined"
          margin="normal"
          placeholder="e.g., Oral"
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: selectBoxHeight,
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
            '& .MuiInputBase-input': { 
              textTransform: 'uppercase' 
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Duration"
          value={medicationForm.duration}
          onChange={(e) => handleMedicationFormChange('duration', e.target.value.toUpperCase())}
          variant="outlined"
          margin="normal"
          placeholder="e.g., 7 days"
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: selectBoxHeight,
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
            '& .MuiInputBase-input': { 
              textTransform: 'uppercase' 
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Instructions"
          value={medicationForm.instructions}
          onChange={(e) => handleMedicationFormChange('instructions', e.target.value.toUpperCase())}
          variant="outlined"
          margin="normal"
          multiline
          rows={2}
          placeholder="Special instructions..."
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
            '& .MuiInputBase-input': { 
              textTransform: 'uppercase' 
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <Box display="flex" gap={1} mt={2}>
          <MDButton
            variant="outlined"
            color="logo"
            startIcon={<AddIcon />}
            onClick={addMedication}
            disabled={!medicationForm.name}
            sx={{
              borderColor: "#00798E",
              color: "#00798E",
              "&:hover": { 
                borderColor: "#005f6b",
                backgroundColor: "rgba(0, 121, 142, 0.1)"
              },
            }}
          >
            Add Medication
          </MDButton>
        </Box>
      </Grid>
    </Grid>
  );

  // Render investigation form
  const renderInvestigationForm = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Investigation Name"
          value={investigationForm.name}
          onChange={(e) => handleInvestigationFormChange('name', e.target.value)}
          variant="outlined"
          margin="normal"
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: selectBoxHeight,
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <FormControl fullWidth margin="normal">
          <InputLabel sx={{ color: '#00798E' }}>Priority</InputLabel>
          <Select
            value={investigationForm.priority}
            onChange={(e) => handleInvestigationFormChange('priority', e.target.value)}
            label="Priority"
            sx={{
              height: selectBoxHeight,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00798E' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00798E' },
            }}
          >
            <MenuItem value="LOW">Low</MenuItem>
            <MenuItem value="NORMAL">Normal</MenuItem>
            <MenuItem value="HIGH">High</MenuItem>
            <MenuItem value="URGENT">Urgent</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={2}>
        <FormControl fullWidth margin="normal">
          <InputLabel sx={{ color: '#00798E' }}>Status</InputLabel>
          <Select
            value={investigationForm.status}
            onChange={(e) => handleInvestigationFormChange('status', e.target.value)}
            label="Status"
            sx={{
              height: selectBoxHeight,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00798E' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00798E' },
            }}
          >
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
            <MenuItem value="CANCELLED">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <Box display="flex" gap={1} mt={2}>
          <MDButton
            variant="outlined"
            color="logo"
            startIcon={<AddIcon />}
            onClick={addInvestigation}
            disabled={!investigationForm.name}
            sx={{
              borderColor: "#00798E",
              color: "#00798E",
              "&:hover": { 
                borderColor: "#005f6b",
                backgroundColor: "rgba(0, 121, 142, 0.1)"
              },
            }}
          >
            Add Investigation
          </MDButton>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Description"
          value={investigationForm.description}
          onChange={(e) => handleInvestigationFormChange('description', e.target.value.toUpperCase())}
          variant="outlined"
          margin="normal"
          multiline
          rows={2}
          placeholder="Investigation description..."
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
            '& .MuiInputBase-input': { 
              textTransform: 'uppercase' 
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Instructions"
          value={investigationForm.instructions}
          onChange={(e) => handleInvestigationFormChange('instructions', e.target.value.toUpperCase())}
          variant="outlined"
          margin="normal"
          multiline
          rows={2}
          placeholder="Special instructions..."
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
            '& .MuiInputBase-input': { 
              textTransform: 'uppercase' 
            },
          }}
        />
      </Grid>
    </Grid>
  );

  // Render observation form
  const renderObservationForm = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Observation Name"
          value={observationForm.name}
          onChange={(e) => handleObservationFormChange('name', e.target.value)}
          variant="outlined"
          margin="normal"
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: selectBoxHeight,
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Value"
          value={observationForm.value}
          onChange={(e) => handleObservationFormChange('value', e.target.value)}
          variant="outlined"
          margin="normal"
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: selectBoxHeight,
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Box display="flex" gap={1} mt={2}>
          <MDButton
            variant="outlined"
            color="logo"
            startIcon={<AddIcon />}
            onClick={addObservation}
            disabled={!observationForm.name}
            sx={{
              borderColor: "#00798E",
              color: "#00798E",
              "&:hover": { 
                borderColor: "#005f6b",
                backgroundColor: "rgba(0, 121, 142, 0.1)"
              },
            }}
          >
            Add Observation
          </MDButton>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Description"
          value={observationForm.description}
          onChange={(e) => handleObservationFormChange('description', e.target.value.toUpperCase())}
          variant="outlined"
          margin="normal"
          multiline
          rows={2}
          placeholder="Observation description..."
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
            '& .MuiInputBase-input': { 
              textTransform: 'uppercase' 
            },
          }}
        />
      </Grid>
    </Grid>
  );

  // Render scan form
  const renderScanForm = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Scan Name"
          value={scanForm.name}
          onChange={(e) => handleScanFormChange('name', e.target.value.toUpperCase())}
          variant="outlined"
          margin="normal"
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: selectBoxHeight,
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
            '& .MuiInputBase-input': { 
              textTransform: 'uppercase' 
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <FormControl fullWidth margin="normal">
          <InputLabel sx={{ color: '#00798E' }}>Priority</InputLabel>
          <Select
            value={scanForm.priority}
            onChange={(e) => handleScanFormChange('priority', e.target.value)}
            label="Priority"
            sx={{
              height: selectBoxHeight,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00798E' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00798E' },
            }}
          >
            <MenuItem value="LOW">Low</MenuItem>
            <MenuItem value="NORMAL">Normal</MenuItem>
            <MenuItem value="HIGH">High</MenuItem>
            <MenuItem value="URGENT">Urgent</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={2}>
        <FormControl fullWidth margin="normal">
          <InputLabel sx={{ color: '#00798E' }}>Status</InputLabel>
          <Select
            value={scanForm.status}
            onChange={(e) => handleScanFormChange('status', e.target.value)}
            label="Status"
            sx={{
              height: selectBoxHeight,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00798E' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00798E' },
            }}
          >
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
            <MenuItem value="CANCELLED">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <Box display="flex" gap={1} mt={2}>
          <MDButton
            variant="outlined"
            color="logo"
            startIcon={<AddIcon />}
            onClick={addScan}
            disabled={!scanForm.name}
            sx={{
              borderColor: "#00798E",
              color: "#00798E",
              "&:hover": { 
                borderColor: "#005f6b",
                backgroundColor: "rgba(0, 121, 142, 0.1)"
              },
            }}
          >
            Add Scan
          </MDButton>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Description"
          value={scanForm.description}
          onChange={(e) => handleScanFormChange('description', e.target.value)}
          variant="outlined"
          margin="normal"
          multiline
          rows={2}
          placeholder="Scan description..."
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Instructions"
          value={scanForm.instructions}
          onChange={(e) => handleScanFormChange('instructions', e.target.value)}
          variant="outlined"
          margin="normal"
          multiline
          rows={2}
          placeholder="Scan instructions..."
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
          }}
        />
      </Grid>
    </Grid>
  );

  // Render treatment form
  const renderTreatmentForm = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Treatment Name"
          value={treatmentForm.name}
          onChange={(e) => handleTreatmentFormChange('name', e.target.value.toUpperCase())}
          variant="outlined"
          margin="normal"
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: selectBoxHeight,
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
            '& .MuiInputBase-input': { 
              textTransform: 'uppercase' 
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <TextField
          fullWidth
          label="Frequency"
          value={treatmentForm.frequency}
          onChange={(e) => handleTreatmentFormChange('frequency', e.target.value.toUpperCase())}
          variant="outlined"
          margin="normal"
          placeholder="e.g., Daily"
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: selectBoxHeight,
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
            '& .MuiInputBase-input': { 
              textTransform: 'uppercase' 
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Duration"
          value={treatmentForm.duration}
          onChange={(e) => handleTreatmentFormChange('duration', e.target.value.toUpperCase())}
          variant="outlined"
          margin="normal"
          placeholder="e.g., 2 weeks"
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: selectBoxHeight,
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
            '& .MuiInputBase-input': { 
              textTransform: 'uppercase' 
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <FormControl fullWidth margin="normal">
          <InputLabel sx={{ color: '#00798E' }}>Status</InputLabel>
          <Select
            value={treatmentForm.status}
            onChange={(e) => handleTreatmentFormChange('status', e.target.value)}
            label="Status"
            sx={{
              height: selectBoxHeight,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00798E' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00798E' },
            }}
          >
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="DISCONTINUED">Discontinued</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={9}>
        <TextField
          fullWidth
          label="Description"
          value={treatmentForm.description}
          onChange={(e) => handleTreatmentFormChange('description', e.target.value.toUpperCase())}
          variant="outlined"
          margin="normal"
          multiline
          rows={2}
          placeholder="Treatment description..."
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
            '& .MuiInputBase-input': { 
              textTransform: 'uppercase' 
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <Box display="flex" gap={1} mt={2}>
          <MDButton
            variant="outlined"
            color="logo"
            startIcon={<AddIcon />}
            onClick={addTreatment}
            disabled={!treatmentForm.name}
            sx={{
              borderColor: "#00798E",
              color: "#00798E",
              "&:hover": { 
                borderColor: "#005f6b",
                backgroundColor: "rgba(0, 121, 142, 0.1)"
              },
            }}
          >
            Add Treatment
          </MDButton>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Instructions"
          value={treatmentForm.instructions}
          onChange={(e) => handleTreatmentFormChange('instructions', e.target.value.toUpperCase())}
          variant="outlined"
          margin="normal"
          multiline
          rows={2}
          placeholder="Treatment instructions..."
          InputLabelProps={{ sx: { color: '#00798E' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#00798E' },
              '&.Mui-focused fieldset': { borderColor: '#00798E' },
            },
            '& .MuiInputBase-input': { 
              textTransform: 'uppercase' 
            },
          }}
        />
      </Grid>
    </Grid>
  );

  // Render component list for each type
  const renderComponentList = (type, components) => {
    if (components.length === 0) {
      return (
        <Box textAlign="center" py={2}>
          <Typography variant="body2" color="textSecondary">
            No items added yet
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 2 }}>
        {components.map((component, index) => (
          <Card key={index} variant="outlined" sx={{ mb: 1, p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box flex={1}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="textSecondary">Name</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {component.name}
                    </Typography>
                  </Grid>
                  
                  {/* Medication-specific fields */}
                  {type === 'prescriptionMedications' && (
                    <>
                      {component.quantity && (
                        <Grid item xs={12} md={2}>
                          <Typography variant="body2" color="textSecondary">Quantity</Typography>
                          <Typography variant="body1">{component.quantity}</Typography>
                        </Grid>
                      )}
                      {component.dosage && (
                        <Grid item xs={12} md={2}>
                          <Typography variant="body2" color="textSecondary">Dosage</Typography>
                          <Typography variant="body1">{component.dosage}</Typography>
                        </Grid>
                      )}
                      {component.frequency && (
                        <Grid item xs={12} md={2}>
                          <Typography variant="body2" color="textSecondary">Frequency</Typography>
                          <Typography variant="body1">{component.frequency}</Typography>
                        </Grid>
                      )}
                      {component.route && (
                        <Grid item xs={12} md={2}>
                          <Typography variant="body2" color="textSecondary">Route</Typography>
                          <Typography variant="body1">{component.route}</Typography>
                        </Grid>
                      )}
                      {component.duration && (
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2" color="textSecondary">Duration</Typography>
                          <Typography variant="body1">{component.duration}</Typography>
                        </Grid>
                      )}
                      {component.status && (
                        <Grid item xs={12} md={2}>
                          <Typography variant="body2" color="textSecondary">Status</Typography>
                          <Typography variant="body1">{component.status}</Typography>
                        </Grid>
                      )}
                    </>
                  )}
                  
                  {/* Observation-specific fields */}
                  {type === 'prescriptionObservations' && (
                    <>
                      {component.value && (
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2" color="textSecondary">Value</Typography>
                          <Typography variant="body1">{component.value}</Typography>
                        </Grid>
                      )}
                    </>
                  )}
                  
                  {/* Investigation/Scan-specific fields */}
                  {(type === 'prescriptionInvestigations' || type === 'prescriptionScans') && (
                    <>
                      {component.priority && (
                        <Grid item xs={12} md={2}>
                          <Typography variant="body2" color="textSecondary">Priority</Typography>
                          <Typography variant="body1">{component.priority}</Typography>
                        </Grid>
                      )}
                      {component.status && (
                        <Grid item xs={12} md={2}>
                          <Typography variant="body2" color="textSecondary">Status</Typography>
                          <Typography variant="body1">{component.status}</Typography>
                        </Grid>
                      )}
                    </>
                  )}
                  
                  {/* Treatment-specific fields */}
                  {type === 'prescriptionTreatments' && (
                    <>
                      {component.frequency && (
                        <Grid item xs={12} md={2}>
                          <Typography variant="body2" color="textSecondary">Frequency</Typography>
                          <Typography variant="body1">{component.frequency}</Typography>
                        </Grid>
                      )}
                      {component.duration && (
                        <Grid item xs={12} md={2}>
                          <Typography variant="body2" color="textSecondary">Duration</Typography>
                          <Typography variant="body1">{component.duration}</Typography>
                        </Grid>
                      )}
                      {component.status && (
                        <Grid item xs={12} md={2}>
                          <Typography variant="body2" color="textSecondary">Status</Typography>
                          <Typography variant="body1">{component.status}</Typography>
                        </Grid>
                      )}
                    </>
                  )}
                  
                  {/* Common fields */}
                  {component.description && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Description</Typography>
                      <Typography variant="body1">{component.description}</Typography>
                    </Grid>
                  )}
                  {component.instructions && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Instructions</Typography>
                      <Typography variant="body1">{component.instructions}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  onClick={() => removeComponent(type, index)}
                  sx={{ 
                    color: "#f44336",
                    "&:hover": { backgroundColor: "rgba(244, 67, 54, 0.04)" },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Card>
        ))}
      </Box>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loading} />
      
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              {/* Header */}
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                bgColor="logo"
                borderRadius="lg"
                coloredShadow="logo"
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center">
                    <IconButton
                      onClick={() => navigate(`/prescription/patient/${patientCode}/${coupleCode || 'undefined'}`)}
                      sx={{ color: 'white', mr: 1 }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <MDTypography variant="h6" color="white">
                      Manage Prescription Components
                    </MDTypography>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1}>
                    <Button
                      size="small"
                      startIcon={<ScannerIcon />}
                      onClick={() => setScanDialogOpen(true)}
                      sx={{
                        background: 'rgba(255,255,255,0.18)',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        border: '1px solid rgba(255,255,255,0.45)',
                        px: 1.5,
                        py: 0.6,
                        borderRadius: 2,
                        whiteSpace: 'nowrap',
                        textTransform: 'none',
                        '&:hover': { background: 'rgba(255,255,255,0.30)' },
                      }}
                    >
                      Scan Prescription
                    </Button>
                    {prescription && (
                      <Chip
                        label={prescription.status}
                        color={prescription.status === 'DRAFT' ? 'default' : 'success'}
                        variant="filled"
                        sx={{ color: 'white', fontWeight: 'bold' }}
                      />
                    )}
                  </Box>
                </Box>
              </MDBox>

              {/* Prescription Info */}
              <MDBox p={2} sx={{ backgroundColor: 'rgba(0, 121, 142, 0.05)' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      Prescription Code
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {prescription?.prescription_code || 'Loading...'}
                    </Typography>
                  </Grid>
                  {patientInfo && (
                    <>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="textSecondary">
                          Patient Name
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {patientInfo.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography variant="body2" color="textSecondary">
                          Patient Code
                        </Typography>
                        <Typography variant="body1">
                          {patientInfo.external_code}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} md={2}>
                    <Typography variant="body2" color="textSecondary">
                      Created Date
                    </Typography>
                    <Typography variant="body1">
                      {prescription?.created_at ? formatDateDDMMYYYY(prescription.created_at) : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={2} display="flex" justifyContent="flex-end">
                    <MDButton
                      variant="outlined"
                      color="logo"
                      size="small"
                      startIcon={<EventNoteIcon />}
                      onClick={async () => {
                        if (patientInfo?.id || prescription?.irm_contacts_id) {
                          await loadDoctorsForFollowUp(patientInfo?.id || prescription?.irm_contacts_id);
                        }
                        setFollowUpOpen(true);
                      }}
                      sx={{
                        borderColor: "#00798E",
                        color: "#00798E",
                        whiteSpace: "nowrap",
                        "&:hover": {
                          borderColor: "#005f6b",
                          backgroundColor: "rgba(0, 121, 142, 0.08)",
                        },
                      }}
                    >
                      Follow Up
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Main Content */}
              <MDBox p={3}>
                {/* Tabs for component types */}
                <Paper sx={{ mb: 2 }}>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{
                      '& .MuiTab-root': {
                        color: '#00798E',
                        minHeight: '60px',
                        flexDirection: 'row',
                        '& .MuiSvgIcon-root': {
                          marginRight: 1,
                          marginBottom: 0,
                          color: '#00798E !important',
                        },
                        '&.Mui-selected': {
                          color: 'white !important',
                          backgroundColor: '#00798E',
                          fontWeight: 'bold',
                          '& .MuiSvgIcon-root': {
                            color: 'white !important',
                          },
                          '& .MuiTab-wrapper': {
                            color: 'white !important',
                          },
                        },
                        '&:hover:not(.Mui-selected)': {
                          backgroundColor: 'rgba(0, 121, 142, 0.1)',
                        },
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#00798E',
                        height: '4px',
                      },
                    }}
                  >
                    <Tab 
                      icon={getComponentIcon('medications')} 
                      label={`Medications (${prescriptionComponents.prescriptionMedications.length})`}
                    />
                    <Tab 
                      icon={getComponentIcon('investigations')} 
                      label={`Investigations (${prescriptionComponents.prescriptionInvestigations.length})`}
                    />
                    <Tab 
                      icon={getComponentIcon('observations')} 
                      label={`Observations (${prescriptionComponents.prescriptionObservations.length})`}
                    />
                    <Tab 
                      icon={getComponentIcon('scans')} 
                      label={`Scans (${prescriptionComponents.prescriptionScans.length})`}
                    />
                    <Tab 
                      icon={getComponentIcon('treatments')} 
                      label={`Treatments (${prescriptionComponents.prescriptionTreatments.length})`}
                    />
                  </Tabs>
                </Paper>

                {/* Tab Content */}
                <Box sx={{ mb: 3 }}>
                  {activeTab === 0 && (
                    <Box>
                      <MDTypography variant="h6" gutterBottom>
                        Current Medications ({prescriptionComponents.prescriptionMedications.length})
                      </MDTypography>
                      {renderComponentList('prescriptionMedications', prescriptionComponents.prescriptionMedications)}
                      
                      <Card sx={{ mt: 2, p: 3 }}>
                        <MDTypography variant="h6" gutterBottom>
                          Add New Medication
                        </MDTypography>
                        {renderMedicationForm()}
                      </Card>
                    </Box>
                  )}
                  
                  {activeTab === 1 && (
                    <Box>
                      <MDTypography variant="h6" gutterBottom>
                        Current Investigations ({prescriptionComponents.prescriptionInvestigations.length})
                      </MDTypography>
                      {renderComponentList('prescriptionInvestigations', prescriptionComponents.prescriptionInvestigations)}
                      
                      <Card sx={{ mt: 2, p: 3 }}>
                        <MDTypography variant="h6" gutterBottom>
                          Add New Investigation
                        </MDTypography>
                        {renderInvestigationForm()}
                      </Card>
                    </Box>
                  )}
                  
                  {activeTab === 2 && (
                    <Box>
                      <MDTypography variant="h6" gutterBottom>
                        Current Observations ({prescriptionComponents.prescriptionObservations.length})
                      </MDTypography>
                      {renderComponentList('prescriptionObservations', prescriptionComponents.prescriptionObservations)}
                      
                      <Card sx={{ mt: 2, p: 3 }}>
                        <MDTypography variant="h6" gutterBottom>
                          Add New Observation
                        </MDTypography>
                        {renderObservationForm()}
                      </Card>
                    </Box>
                  )}
                  
                  {activeTab === 3 && (
                    <Box>
                      <MDTypography variant="h6" gutterBottom>
                        Current Scans ({prescriptionComponents.prescriptionScans.length})
                      </MDTypography>
                      {renderComponentList('prescriptionScans', prescriptionComponents.prescriptionScans)}
                      
                      <Card sx={{ mt: 2, p: 3 }}>
                        <MDTypography variant="h6" gutterBottom>
                          Add New Scan
                        </MDTypography>
                        {renderScanForm()}
                      </Card>
                    </Box>
                  )}
                  
                  {activeTab === 4 && (
                    <Box>
                      <MDTypography variant="h6" gutterBottom>
                        Current Treatments ({prescriptionComponents.prescriptionTreatments.length})
                      </MDTypography>
                      {renderComponentList('prescriptionTreatments', prescriptionComponents.prescriptionTreatments)}
                      
                      <Card sx={{ mt: 2, p: 3 }}>
                        <MDTypography variant="h6" gutterBottom>
                          Add New Treatment
                        </MDTypography>
                        {renderTreatmentForm()}
                      </Card>
                    </Box>
                  )}
                </Box>

                {/* Save Button */}
                <Box display="flex" justifyContent="center" mt={4}>
                  <MDButton
                    variant="gradient"
                    color="logo"
                    size="large"
                    startIcon={<SaveIcon />}
                    onClick={handleSavePrescription}
                    disabled={loading || !Object.values(newComponents).some(componentArray => componentArray.length > 0)}
                  >
                    Save New Components
                  </MDButton>
                </Box>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* AI Prescription Scan dialog */}
      <ScanPrescriptionDialog
        open={scanDialogOpen}
        onClose={() => setScanDialogOpen(false)}
        prescriptionId={prescription?.id}
        prescriptionCode={prescription?.prescription_code || prescriptionCode}
        onApplyMedications={handleAiApplyMedications}
        onApplyInvestigations={handleAiApplyInvestigations}
        onApplyVitals={handleAiApplyVitals}
      />

      {/* Follow-Up creation dialog */}
      <FollowUpDialog
        open={followUpOpen}
        onClose={() => setFollowUpOpen(false)}
        onSuccess={() => {
          Swal.fire({
            icon: 'success',
            title: 'Follow-Up Created',
            text: 'The follow-up has been scheduled successfully.',
            confirmButtonColor: '#00798E',
            confirmButtonText: 'OK',
          });
        }}
        contactId={patientInfo?.id || prescription?.irm_contacts_id}
        doctors={doctors}
        defaultDoctorId={defaultDoctorId || prescription?.irm_users_id}
        prescriptionId={prescription?.id}
        treatmentLine={
          [prescription?.probable_diagonosis_female, prescription?.probable_diagonosis_male]
            .filter(Boolean)
            .map(t => t.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim())
            .join(' | ') || null
        }
        currentUserId={currentUserId}
      />
    </DashboardLayout>
  );
}

export default ManagePrescription;
